import React, { useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { InstanceXFeature } from '../../types';
import RelativeTime from '../RelativeTime';
import { TableCard, TableHeader, TableViewport, SortableTh, EmptyRow, tdClass, SortOrder } from '../table/TableKit';

interface Props {
  instanceFeatures: InstanceXFeature[];
  changes: Map<string, boolean>;
  onFeatureChange: (instanceId: number, featureKey: string, isEnabled: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedRef: string | null;
  onSelectRef: (ref: string | null) => void;
  total: number;
  offset: number;
  pageSize: number;
  onOffsetChange: (offset: number) => void;
  onPageSizeChange: (size: number) => void;
  sort: string;
  order: SortOrder;
  onSort: (sort: string, order: SortOrder) => void;
}

const featureColumns = [
  { key: 'analyzer_web', label: 'Analyzer WEB' },
  { key: 'analyzer_cli', label: 'Analyzer CLI' },
  { key: 'analyzer_api', label: 'Analyzer API' },
  { key: 'converter_web', label: 'Converter WEB' },
  { key: 'converter_cli', label: 'Converter CLI' },
  { key: 'converter_api', label: 'Converter API' },
];

const InstanceFeaturesTab: React.FC<Props> = ({ instanceFeatures, changes, onFeatureChange, searchQuery, onSearchChange, selectedRef, onSelectRef, total, offset, pageSize, onOffsetChange, onPageSizeChange, sort, order, onSort }) => {

  // Helper function to get effective value including pending changes
  const getEffectiveValue = useCallback((item: InstanceXFeature, featureKey: string): boolean => {
    const changeKey = `${item.instance_id}#${featureKey}`;
    if (changes.has(changeKey)) {
      return changes.get(changeKey)!;
    }
    // Access the feature value dynamically
    return (item as any)[featureKey] || false;
  }, [changes]);

  const hasChange = (item: InstanceXFeature): boolean => {
    // Check if any feature for this instance has been changed
    return featureColumns.some(({ key }) => {
      const featureKey = key;
      const changeKey = `${item.instance_id}#${featureKey}`;
      return changes.has(changeKey);
    });
  };

  const handleRefClick = (ref: string) => {
    onSelectRef(selectedRef === ref ? null : ref);
  };

  return (
    <TableCard>
      <TableHeader
        title="Instance Features"
        search={{ value: searchQuery, onChange: onSearchChange }}
        pagination={{
          offset,
          limit: pageSize,
          total,
          onOffsetChange,
          onLimitChange: onPageSizeChange,
        }}
      >
        {selectedRef && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary">Filtered by:</span>
            <span
              onClick={() => onSelectRef(null)}
              className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium cursor-pointer hover:bg-primary/30"
            >
              {selectedRef}
              <XMarkIcon className="w-3 h-3" />
            </span>
          </div>
        )}
      </TableHeader>

      <TableViewport>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {featureColumns.map(({ key, label }) => (
                <SortableTh
                  key={key}
                  label={label}
                  sortKey={key}
                  sort={sort}
                  order={order}
                  onSort={onSort}
                />
              ))}
              <SortableTh
                label="Instance Key"
                sortKey="instance_key"
                sort={sort}
                order={order}
                onSort={onSort}
                firstOrder="asc"
              />
              <SortableTh
                label="Referrals"
                sortKey="referrals"
                sort={sort}
                order={order}
                onSort={onSort}
              />
              <SortableTh
                label="Contact Email"
                sortKey="contact_email"
                sort={sort}
                order={order}
                onSort={onSort}
                firstOrder="asc"
              />
              <SortableTh
                label="Created"
                sortKey="created_at"
                sort={sort}
                order={order}
                onSort={onSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {instanceFeatures.length === 0 ? (
              <EmptyRow colSpan={10} text="No instance features found." />
            ) : (
              instanceFeatures.map((item) => (
                <tr
                  key={item.instance_id}
                  className={`hover:bg-body-bg transition-colors ${hasChange(item) ? 'bg-yellow-900/20' : ''}`}
                >
                  {featureColumns.map(({ key }) => (
                    <td key={key} className={tdClass}>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={getEffectiveValue(item, key)}
                          onChange={(e) => onFeatureChange(item.instance_id, key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </td>
                  ))}
                  <td className={tdClass}>{item.instance_key}</td>
                  <td className={`${tdClass} whitespace-normal`}>
                    {item.refs && item.refs.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {item.refs.map((ref) => (
                          <span
                            key={ref}
                            onClick={() => handleRefClick(ref)}
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                              selectedRef === ref
                                ? 'bg-primary text-white'
                                : 'bg-secondary/20 text-body-color hover:bg-secondary/30'
                            }`}
                          >
                            {ref}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-secondary">-</span>
                    )}
                  </td>
                  <td className={tdClass}>{item.contact_email || '-'}</td>
                  <td className={`${tdClass} text-secondary`}><RelativeTime value={item.created_at} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableViewport>
    </TableCard>
  );
};

export default InstanceFeaturesTab;
